import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { ScrollView, StyleSheet, View } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { DisplayNamesForm } from "@/components/tenant/forms";
import { tenantService } from "@/services/tenantService";
import { TenantType } from "@/types/db/tenants";
import useToast from "@/hooks/useToast";
import useAuth from "@/hooks/useAuth";
import { useDisplayNamesHeader } from "@/contexts/DisplayNamesHeaderContext";

export default function DisplayNamesPage() {
  const { colors } = useTheme();
  const { tenantId } = useAuth();
  const toast = useToast();
  const { setHeaderActions, clearHeaderActions } = useDisplayNamesHeader();
  const [meta, setMeta] = useState<TenantType["meta_data"] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const baseline = useRef<TenantType["meta_data"] | null>(null);
  const metaRef = useRef<TenantType["meta_data"] | null>(null);

  // Keep metaRef in sync with meta state
  useEffect(() => {
    metaRef.current = meta;
  }, [meta]);

  const dirty = useMemo(() => {
    if (!meta || !baseline.current) return false;
    return JSON.stringify(meta) !== JSON.stringify(baseline.current);
  }, [meta]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const metaData = await tenantService.getTenantMetaData();
      setMeta(metaData);
      baseline.current = metaData;
    } catch (e: any) {
      const errorMessage = e?.message ?? "Failed to load display names.";
      setError(errorMessage);
      setMeta(null);
      baseline.current = null;

      toast.showToast({
        title: "Load failed",
        message: e?.message || "Unable to load display names",
        toastType: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = useCallback(async () => {
    const currentMeta = metaRef.current;
    if (!currentMeta || !tenantId) return;

    setSaving(true);
    try {
      await tenantService.updateTenantMetaData(tenantId, currentMeta);
      baseline.current = currentMeta;

      toast.showToast({
        title: "Settings saved",
        message: "Your display names have been updated",
        toastType: "success",
      });
    } catch (e: any) {
      toast.showToast({
        title: "Save failed",
        message: e?.message || "Unable to save display names",
        toastType: "error",
      });
    } finally {
      setSaving(false);
    }
  }, [tenantId, toast]);

  const handleReset = useCallback(() => {
    if (baseline.current) {
      setMeta({ ...baseline.current });
    }
  }, []);

  // Load data on mount
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Register header actions with context
  useEffect(() => {
    setHeaderActions({
      dirty,
      saving,
      onSave: handleSave,
      onReset: handleReset,
    });
  }, [dirty, saving, handleSave, handleReset, setHeaderActions]);

  // Clear header actions on unmount
  useEffect(() => {
    return () => {
      clearHeaderActions();
    };
  }, [clearHeaderActions]);

  if (loading) {
    return (
      <ScrollView contentContainerStyle={styles.centered}>
        <ThemedText>Loading display names...</ThemedText>
      </ScrollView>
    );
  }

  if (error || !meta) {
    return (
      <ScrollView contentContainerStyle={styles.centered}>
        <ThemedText type="defaultSemiBold">Something went wrong</ThemedText>
        <ThemedText style={{ opacity: 0.8, marginTop: 6 }}>{error || "Unknown error"}</ThemedText>
        <Button text="Retry" icon="refresh" style={{ marginTop: 12 }} onPress={load} />
      </ScrollView>
    );
  }

  const handleDisplayNamesChange = (display_names: any) => {
    setMeta((m) => ({
      ...m,
      info: { ...m?.info, display_names },
    }));
  };

  return (
    <ScrollView style={styles.container}>
      <DisplayNamesForm displayNames={meta?.info?.display_names} onChange={handleDisplayNamesChange} colors={colors} />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    marginTop: 16,
  },
  centered: {
    alignItems: "center",
    gap: 8,
  },
});
